import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, addDoc, serverTimestamp,
  doc, deleteDoc
} from 'firebase/firestore';
import { uploadToCloudinary, uploadCertificateToCloudinary } from '../../config/cloudinary';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';
import { v4 as uuidv4 } from 'uuid';
import { saveDraft } from '../../utils/draftUtils';

export default function AddStudent() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const draftId = location.state?.draft?.id || null;

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [certificate, setCertificate] = useState(null);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [draftSaved, setDraftSaved] = useState('');
  const [useCustomClass, setUseCustomClass] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    class: '',
    school: '',
    region: '',
    district: '',
    ward: '',
    parentName: '',
    parentPhone: '',
    teacherName: '',
    teacherPhone: '',
    teacherEmail: '',
    teacherFeedback: '',
    healthStatus: '',
    notes: '',
    hasBirthCertificate: '',
  });

  // Load draft if coming from drafts page
  useEffect(() => {
    if (location.state?.draft) {
      const draft = location.state.draft;
      setForm(prev => ({ ...prev, ...draft.data }));
      const draftClass = draft.data?.class;
      if (draftClass && !['1','2','3','4','5','6','7'].includes(draftClass)) {
        setUseCustomClass(true);
      }
    }
  }, []);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handlePhoto(e) {
    const file = e.target.files[0];
    if (file) {
      setPhoto(file);
      setPhotoPreview(URL.createObjectURL(file));
    }
  }

  function handleCertificate(e) {
    const file = e.target.files[0];
    if (file) {
      setCertificate(file);
      if (file.type.startsWith('image/')) {
        setCertificatePreview(URL.createObjectURL(file));
      } else {
        setCertificatePreview('pdf');
      }
    }
  }

  function generateStudentId() {
    const year = new Date().getFullYear().toString().slice(-2);
    const unique = uuidv4().slice(0, 6).toUpperCase();
    return `IYA-STU-${year}-${unique}`;
  }

  function calculateCompletion(data) {
    const required = [
      'firstName', 'lastName', 'dateOfBirth',
      'gender', 'class', 'school',
      'region', 'district', 'parentName',
      'parentPhone', 'teacherName', 'hasBirthCertificate'
    ];
    const filled = required.filter(f => data[f]).length;
    return Math.round((filled / required.length) * 100);
  }

  async function handleSaveDraft() {
    setSavingDraft(true);
    setDraftSaved('');
    setError('');
    try {
      const { completion } = await saveDraft({
        type: 'student',
        data: form,
        currentUser,
        draftId,
      });
      setDraftSaved(`Draft saved! ${completion}% complete`);
      setTimeout(() => setDraftSaved(''), 3000);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not save draft. Try again.');
    }
    setSavingDraft(false);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const studentId = generateStudentId();
      let photoURL = '';
      let certificateURL = '';

      if (photo) {
        photoURL = await uploadToCloudinary(photo);
      }

      if (certificate) {
        certificateURL = await uploadCertificateToCloudinary(
          certificate
        );
      }

      await addDoc(collection(db, 'students'), {
        ...form,
        studentId,
        photoURL,
        certificateURL,
        status: 'Active',
        hasBirthCertificate: form.hasBirthCertificate,
        project: 'Project One',
        registeredBy: currentUser.uid,
        createdAt: serverTimestamp(),
        progress: {
          academic: 0,
          behaviour: 0,
          health: 0,
          community: 0,
          lifeSkills: 0,
        },
        support: {
          schoolFees: false,
          uniform: false,
          food: false,
          stationery: false,
        },
      });

      // Delete draft if it existed
      if (draftId) {
        try {
          await deleteDoc(doc(db, 'drafts', draftId));
        } catch (e) {
          console.log('Draft cleanup:', e);
        }
      }

      setSuccess(
        `Student registered successfully! ID: ${studentId}`
      );
      setUseCustomClass(false);
      setForm({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: '',
        class: '',
        school: '',
        region: '',
        district: '',
        ward: '',
        parentName: '',
        parentPhone: '',
        teacherName: '',
        teacherPhone: '',
        teacherEmail: '',
        teacherFeedback: '',
        healthStatus: '',
        notes: '',
        hasBirthCertificate: '',
      });
      setPhoto(null);
      setPhotoPreview(null);
      setCertificate(null);
      setCertificatePreview(null);

    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Register New Student
            </div>
            <div style={styles.pageSub}>
              Fill in all details — a unique ID will be
              generated automatically
            </div>
          </div>
          <div style={styles.headerBtns}>
            {draftId && (
              <div style={styles.draftBadge}>
                Continuing Draft
              </div>
            )}
            <button
              style={styles.btnOutline}
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
          </div>
        </div>

        {/* DRAFT PROGRESS BAR */}
        <div style={styles.draftProgress}>
          <div style={styles.draftProgressHeader}>
            <span style={styles.draftProgressLabel}>
              Form Completion
            </span>
            <span style={{
              ...styles.draftProgressValue,
              color: calculateCompletion(form) >= 80
                ? '#14452F'
                : calculateCompletion(form) >= 50
                ? '#F57C00'
                : '#C62828'
            }}>
              {calculateCompletion(form)}%
            </span>
          </div>
          <div style={styles.draftProgressBg}>
            <div style={{
              ...styles.draftProgressFill,
              width: `${calculateCompletion(form)}%`,
              background: calculateCompletion(form) >= 80
                ? 'linear-gradient(90deg, #14452F, #1F6B45)'
                : calculateCompletion(form) >= 50
                ? 'linear-gradient(90deg, #F57C00, #FFA726)'
                : 'linear-gradient(90deg, #C62828, #E53935)'
            }} />
          </div>
        </div>

        {draftSaved && (
          <div style={styles.draftSavedMsg}>{draftSaved}</div>
        )}
        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>

          {/* PHOTO UPLOAD */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Student Photo
            </div>
            <div style={styles.photoArea}>
              <div style={styles.photoPreview}>
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    style={styles.photoImg}
                  />
                ) : (
                  <span style={styles.photoPlaceholder}>
                    
                  </span>
                )}
              </div>
              <div>
                <label style={styles.uploadBtn}>
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhoto}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={styles.uploadHint}>
                  JPG or PNG · Max 5MB
                </div>
              </div>
            </div>
          </div>

          {/* PERSONAL INFO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Personal Information
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  First Name *
                </label>
                <input
                  style={styles.formInput}
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="e.g. Amina"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Last Name *
                </label>
                <input
                  style={styles.formInput}
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="e.g. Juma"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Date of Birth *
                </label>
                <input
                  style={styles.formInput}
                  name="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Gender *
                </label>
                <select
                  style={styles.formInput}
                  name="gender"
                  value={form.gender}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select gender</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Class *
                </label>
                <select
                  style={styles.formInput}
                  name="class"
                  value={
                    useCustomClass
                      ? 'other'
                      : form.class
                  }
                  onChange={(e) => {
                    if (e.target.value === 'other') {
                      setUseCustomClass(true);
                      setForm({ ...form, class: '' });
                    } else {
                      setUseCustomClass(false);
                      setForm({ ...form, class: e.target.value });
                    }
                  }}
                  required={!useCustomClass}
                >
                  <option value="">Select class</option>
                  <option value="1">Class 1</option>
                  <option value="2">Class 2</option>
                  <option value="3">Class 3</option>
                  <option value="4">Class 4</option>
                  <option value="5">Class 5</option>
                  <option value="6">Class 6</option>
                  <option value="7">Class 7</option>
                  <option value="other">Other (type manually)</option>
                </select>
                {useCustomClass && (
                  <input
                    style={{ ...styles.formInput, marginTop: '8px' }}
                    name="class"
                    value={form.class}
                    onChange={handleChange}
                    placeholder="e.g. Maandalizi, Standard 8"
                    required
                    autoFocus
                  />
                )}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  School Name *
                </label>
                <input
                  style={styles.formInput}
                  name="school"
                  value={form.school}
                  onChange={handleChange}
                  placeholder="e.g. Kinondoni Primary"
                  required
                />
              </div>
            </div>
          </div>

          {/* CLASS TEACHER */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Class Teacher
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Full Name *
                </label>
                <input
                  style={styles.formInput}
                  name="teacherName"
                  value={form.teacherName}
                  onChange={handleChange}
                  placeholder="e.g. Mr. Joseph Mwanga"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Phone
                </label>
                <input
                  style={styles.formInput}
                  name="teacherPhone"
                  value={form.teacherPhone}
                  onChange={handleChange}
                  placeholder="+255 7XX XXX XXX"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Teacher Email
                </label>
                <input
                  style={styles.formInput}
                  name="teacherEmail"
                  value={form.teacherEmail}
                  onChange={handleChange}
                  placeholder="teacher@school.ac.tz"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Feedback from Teacher
                </label>
                <input
                  style={styles.formInput}
                  name="teacherFeedback"
                  value={form.teacherFeedback}
                  onChange={handleChange}
                  placeholder="Initial teacher comments..."
                />
              </div>
            </div>
          </div>

          {/* LOCATION */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>Location</div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Region *
                </label>
                <input
                  style={styles.formInput}
                  name="region"
                  value={form.region}
                  onChange={handleChange}
                  placeholder="e.g. Dar es Salaam"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  District *
                </label>
                <input
                  style={styles.formInput}
                  name="district"
                  value={form.district}
                  onChange={handleChange}
                  placeholder="e.g. Kinondoni"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Ward</label>
                <input
                  style={styles.formInput}
                  name="ward"
                  value={form.ward}
                  onChange={handleChange}
                  placeholder="e.g. Mwananyamala"
                />
              </div>
            </div>
          </div>

          {/* PARENT INFO */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Parent / Guardian
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Parent Full Name *
                </label>
                <input
                  style={styles.formInput}
                  name="parentName"
                  value={form.parentName}
                  onChange={handleChange}
                  placeholder="e.g. Fatuma Ally"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Parent Phone *
                </label>
                <input
                  style={styles.formInput}
                  name="parentPhone"
                  value={form.parentPhone}
                  onChange={handleChange}
                  placeholder="+255 7XX XXX XXX"
                  required
                />
              </div>
            </div>
          </div>

          {/* BIRTH CERTIFICATE */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Birth Certificate
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Does student have a birth certificate? *
                </label>
                <select
                  style={styles.formInput}
                  name="hasBirthCertificate"
                  value={form.hasBirthCertificate}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select option</option>
                  <option value="yes">
                    Yes — has certificate
                  </option>
                  <option value="no">
                    No — needs certificate
                  </option>
                  <option value="processing">
                    In process
                  </option>
                </select>
              </div>
            </div>
            {form.hasBirthCertificate === 'yes' && (
              <div style={{ marginTop: '16px' }}>
                <label style={styles.uploadBtn}>
                  Upload Birth Certificate
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleCertificate}
                    style={{ display: 'none' }}
                  />
                </label>
                <div style={styles.uploadHint}>
                  JPG, PNG or PDF accepted
                </div>
                {certificatePreview && (
                  <div style={{ marginTop: '12px' }}>
                    {certificatePreview === 'pdf' ? (
                      <div style={styles.pdfPreview}>
                        PDF Certificate uploaded 
                      </div>
                    ) : (
                      <img
                        src={certificatePreview}
                        alt="Certificate"
                        style={styles.certImg}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            {form.hasBirthCertificate === 'no' && (
              <div style={styles.noCertAlert}>
                This student needs a birth certificate.
                The foundation will help obtain one.
              </div>
            )}
          </div>

          {/* HEALTH & NOTES */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Health & Notes
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Health Status
                </label>
                <select
                  style={styles.formInput}
                  name="healthStatus"
                  value={form.healthStatus}
                  onChange={handleChange}
                >
                  <option value="">Select status</option>
                  <option>Healthy</option>
                  <option>Needs Attention</option>
                  <option>Under Treatment</option>
                </select>
              </div>
              <div style={{
                ...styles.formGroup,
                gridColumn: '1 / -1'
              }}>
                <label style={styles.formLabel}>
                  Additional Notes
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '90px',
                    resize: 'vertical'
                  }}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Any extra information about this student..."
                />
              </div>
            </div>
          </div>

          {/* BUTTONS */}
          <div style={styles.btnRow}>
            <button
              type="button"
              style={styles.draftBtn}
              onClick={handleSaveDraft}
              disabled={savingDraft}
            >
              {savingDraft
                ? 'Saving...'
                : 'Save Draft'}
            </button>
            <button
              type="submit"
              style={styles.submitBtn}
              disabled={loading}
            >
              {loading
                ? 'Registering...'
                : 'Register Student'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  headerBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  draftBadge: {
    background: '#FFF3E0',
    color: '#E65100',
    border: '1px solid #FFE0B2',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
  },
  btnOutline: {
    background: 'transparent',
    color: '#14452F',
    border: '1.5px solid #1D5C3C',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  draftProgress: {
    background: '#fff',
    borderRadius: '12px',
    padding: '14px 18px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  draftProgressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  draftProgressLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  draftProgressValue: {
    fontSize: '12px',
    fontWeight: '700',
  },
  draftProgressBg: {
    height: '8px',
    background: '#E5E7EB',
    borderRadius: '99px',
    overflow: 'hidden',
  },
  draftProgressFill: {
    height: '100%',
    borderRadius: '99px',
    transition: 'width 0.5s ease',
  },
  draftSavedMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '16px',
    fontWeight: '500',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  photoArea: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  photoPreview: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  photoImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  photoPlaceholder: { fontSize: '32px' },
  uploadBtn: {
    display: 'inline-block',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    padding: '9px 18px',
    borderRadius: '9px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(245,124,0,0.25)',
  },
  uploadHint: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginTop: '6px',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  certImg: {
    width: '100%',
    maxWidth: '300px',
    borderRadius: '10px',
    border: '1px solid #E5E7EB',
  },
  pdfPreview: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
  },
  noCertAlert: {
    background: '#FFF3E0',
    border: '1px solid #FFE0B2',
    color: '#E65100',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginTop: '14px',
  },
  btnRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '30px',
  },
  draftBtn: {
    padding: '15px 24px',
    background: 'rgba(20,69,47,0.08)',
    color: '#14452F',
    border: '2px solid #14452F',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    flexShrink: 0,
  },
  submitBtn: {
    flex: 1,
    padding: '15px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(20,69,47,0.3)',
  },
};